import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import CompanyBrandMark from '../../components/layout/CompanyBrandMark';

const EmployeeSetupPassword = ({ defaultMode = 'setup' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Mode: 'setup' (first time) or 'forgot' (reset password)
  const initialMode = searchParams.get('mode') || defaultMode;
  const [mode, setMode] = useState(initialMode);

  // Flow Step: 1 (Identity) -> 2 (OTP) -> 3 (Password)
  const [step, setStep] = useState(1);

  // Form states
  const [employeeId, setEmployeeId] = useState('');
  const [email, setEmail] = useState('');
  const [setupToken, setSetupToken] = useState('');
  
  // OTP input state (6 digits)
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);

  // Password state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Timer for OTP resend (10 minutes max countdown / 60s cooldown for resend)
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Handle Mode Change
  const handleModeToggle = (newMode) => {
    setMode(newMode);
    setStep(1);
    setError(null);
    setSuccessMessage(null);
    setOtpDigits(['', '', '', '', '', '']);
  };

  // Step 1: Submit Identity Verification
  const handleVerifyIdentity = async (e) => {
    e.preventDefault();
    if (!employeeId.trim() || !email.trim()) {
      setError('Please enter both your Employee ID and Email address.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/employee-auth/setup/verify-identity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employeeId.trim(),
          email: email.trim().toLowerCase(),
          mode: mode
        })
      });

      const res = await response.json();

      if (res.success && res.data?.setup_token) {
        setSetupToken(res.data.setup_token);
        setSuccessMessage(res.data.message || `Verification code sent to ${email.trim()}`);
        setStep(2);
        setResendCooldown(60);
      } else {
        setError(res.error || res.message || 'Invalid Employee ID or email');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // OTP Digit Change Handler
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...otpDigits];
    newDigits[index] = value.slice(-1);
    setOtpDigits(newDigits);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasteData)) {
      const digits = pasteData.split('');
      setOtpDigits(digits);
      otpRefs.current[5]?.focus();
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpCode = otpDigits.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the full 6-digit verification code.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/employee-auth/setup/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setup_token: setupToken,
          otp: otpCode
        })
      });

      const res = await response.json();

      if (res.success) {
        setStep(3);
        setSuccessMessage('Verification code confirmed. Now create your new password.');
      } else {
        setError(res.error || res.message || 'Invalid verification code.');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP Code
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/employee-auth/setup/verify-identity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employeeId.trim(),
          email: email.trim().toLowerCase(),
          mode: mode
        })
      });

      const res = await response.json();

      if (res.success && res.data?.setup_token) {
        setSetupToken(res.data.setup_token);
        setOtpDigits(['', '', '', '', '', '']);
        setSuccessMessage(`A new verification code was sent to ${email.trim()}`);
        setResendCooldown(60);
      } else {
        setError(res.error || res.message || 'Failed to resend code.');
      }
    } catch (err) {
      setError('Connection error while resending code.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Set Password
  const handleSetPassword = async (e) => {
    e.preventDefault();

    if (!password) {
      setError('Please enter a password.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match. Please check and try again.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/employee-auth/setup/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setup_token: setupToken,
          password: password
        })
      });

      const res = await response.json();

      if (res.success) {
        navigate('/employee/login', {
          state: {
            successMessage: mode === 'forgot'
              ? 'Password reset successfully! Please sign in with your new password.'
              : 'Password configured successfully! Please sign in with your Employee ID and password.'
          }
        });
      } else {
        setError(res.error || res.message || 'Failed to set password.');
      }
    } catch (err) {
      setError(err.message || 'Connection error while setting password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/40 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center mb-6">
        <Link to="/" className="inline-block mb-4">
          <CompanyBrandMark size="lg" />
        </Link>
        <h2 className="text-2xl font-bold tracking-tight text-foreground">
          Employee Self-Service Portal
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {mode === 'setup' ? 'First-time account password setup' : 'Reset your employee password'}
        </p>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card border border-border py-8 px-6 shadow-xl rounded-xl sm:px-10">
          
          {/* Mode Switcher Tabs */}
          <div className="flex border-b border-border mb-6">
            <button
              type="button"
              onClick={() => handleModeToggle('setup')}
              className={`flex-1 pb-3 text-xs font-semibold text-center border-b-2 transition-colors ${
                mode === 'setup'
                  ? 'border-teal-600 text-teal-600 font-bold'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              First Time Setup
            </button>
            <button
              type="button"
              onClick={() => handleModeToggle('forgot')}
              className={`flex-1 pb-3 text-xs font-semibold text-center border-b-2 transition-colors ${
                mode === 'forgot'
                  ? 'border-teal-600 text-teal-600 font-bold'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Forgot Password
            </button>
          </div>

          {/* Stepper Indicator */}
          <div className="flex items-center justify-between mb-6 px-4">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= 1 ? 'bg-teal-600 text-white' : 'bg-muted text-muted-foreground'
              }`}>
                1
              </div>
              <span className="text-[11px] font-medium hidden sm:inline">Identity</span>
            </div>
            <div className={`flex-1 h-0.5 mx-2 ${step >= 2 ? 'bg-teal-600' : 'bg-muted'}`} />
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= 2 ? 'bg-teal-600 text-white' : 'bg-muted text-muted-foreground'
              }`}>
                2
              </div>
              <span className="text-[11px] font-medium hidden sm:inline">OTP</span>
            </div>
            <div className={`flex-1 h-0.5 mx-2 ${step >= 3 ? 'bg-teal-600' : 'bg-muted'}`} />
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= 3 ? 'bg-teal-600 text-white' : 'bg-muted text-muted-foreground'
              }`}>
                3
              </div>
              <span className="text-[11px] font-medium hidden sm:inline">Password</span>
            </div>
          </div>

          {/* Alert Messages */}
          {error && (
            <div className="mb-4 p-3 bg-rose-500/10 border border-rose-500/20 text-rose-600 rounded-lg text-xs font-semibold flex items-start gap-2">
              <Icon name="AlertCircle" size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {successMessage && !error && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 rounded-lg text-xs font-semibold flex items-start gap-2">
              <Icon name="CheckCircle2" size={16} className="shrink-0 mt-0.5" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* STEP 1: Verify Identity */}
          {step === 1 && (
            <form className="space-y-4" onSubmit={handleVerifyIdentity}>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Employee ID / Code
                </label>
                <Input
                  type="text"
                  placeholder="e.g. EMP-0017"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value.toUpperCase())}
                  required
                  icon="UserCheck"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Email Address (on official record)
                </label>
                <Input
                  type="email"
                  placeholder="e.g. employee@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  icon="Mail"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Verification OTP code will be sent to this email.
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2.5 shadow-sm mt-2"
                iconName={loading ? "Loader2" : "Send"}
                iconPosition="left"
              >
                {loading ? "Verifying..." : "Send Verification Code"}
              </Button>
            </form>
          )}

          {/* STEP 2: Enter OTP */}
          {step === 2 && (
            <form className="space-y-5" onSubmit={handleVerifyOtp}>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-3">
                  Enter the 6-digit verification code sent to <br />
                  <strong className="text-foreground">{email}</strong>
                </p>

                {/* 6 Digit Inputs */}
                <div className="flex justify-center gap-2 my-4" onPaste={handleOtpPaste}>
                  {otpDigits.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (otpRefs.current[idx] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      className="w-10 h-12 text-center text-lg font-bold bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all"
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-1 font-medium"
                >
                  <Icon name="ArrowLeft" size={14} /> Back
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || loading}
                  className={`font-semibold ${
                    resendCooldown > 0
                      ? 'text-muted-foreground cursor-not-allowed'
                      : 'text-teal-600 hover:underline'
                  }`}
                >
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code'}
                </button>
              </div>

              <Button
                type="submit"
                disabled={loading || otpDigits.join('').length !== 6}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2.5 shadow-sm"
                iconName={loading ? "Loader2" : "ShieldCheck"}
                iconPosition="left"
              >
                {loading ? "Verifying Code..." : "Verify Code"}
              </Button>
            </form>
          )}

          {/* STEP 3: Set New Password */}
          {step === 3 && (
            <form className="space-y-4" onSubmit={handleSetPassword}>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    icon="Lock"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Confirm Password
                </label>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  icon="LockCheck"
                />
              </div>

              {/* Password Requirement Checkers */}
              <div className="p-3 bg-muted/60 rounded-lg space-y-1 text-[11px] text-muted-foreground">
                <div className={`flex items-center gap-1.5 ${password.length >= 8 ? 'text-emerald-600 font-semibold' : ''}`}>
                  <Icon name={password.length >= 8 ? "Check" : "Circle"} size={12} />
                  <span>Minimum 8 characters</span>
                </div>
                <div className={`flex items-center gap-1.5 ${password && password === confirmPassword ? 'text-emerald-600 font-semibold' : ''}`}>
                  <Icon name={password && password === confirmPassword ? "Check" : "Circle"} size={12} />
                  <span>Passwords match</span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || password.length < 8 || password !== confirmPassword}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2.5 shadow-sm"
                iconName={loading ? "Loader2" : "KeyRound"}
                iconPosition="left"
              >
                {loading ? "Saving Password..." : "Save Password & Finish"}
              </Button>
            </form>
          )}

          {/* Footer Back to Login */}
          <div className="mt-6 pt-4 border-t border-border text-center">
            <Link
              to="/employee/login"
              className="text-xs font-semibold text-teal-600 hover:underline flex items-center justify-center gap-1"
            >
              <Icon name="ArrowLeft" size={14} /> Return to Employee Sign In
            </Link>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Need assistance? Contact HR Department at your company office.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeSetupPassword;
